/**
 * The dialog shell. Any [data-awdd-dialog-open="{name}"] opens #awdd-dialog-{name},
 * and so does openDialog(name). Every close dispatches awdd-dialog-close on the
 * dialog's .dlg-wrap.
 * Timings match the static build: the CSS runs 260ms in, 160ms out; the panel
 * is hidden again after 180ms.
 */

import { setScrollLock } from './scroll-lock.js';
import { focusables, trapFocus } from './focus-trap.js';

const EXIT_MS = 180;

let openByName = null;

/** Open #awdd-dialog-{name} from script. False when there is no such dialog. */
export function openDialog(name) {
	return openByName ? openByName(name) : false;
}

export default function initDialogs() {
	let current = null;

	const open = (wrap, opener) => {
		if (current) close(current, { restoreFocus: false });
		if (typeof window.awddCloseMenu === 'function') window.awddCloseMenu();

		const panel = wrap.querySelector('.dlg');
		const head = wrap.querySelector('.dlg-head');
		window.clearTimeout(wrap._exitTimer);
		wrap._returnTo = opener || document.activeElement;
		wrap.hidden = false;
		// Closed within two frames of opening: do not re-add is-in to a dialog
		// that is already on its way out.
		requestAnimationFrame(() => requestAnimationFrame(() => {
			if (current !== wrap) return;
			wrap.classList.add('is-in');
		}));
		setScrollLock(wrap, true);
		document.body.dataset.dialogOpen = '1';
		current = wrap;

		const auto = panel.querySelector('[autofocus]');
		(auto || panel).focus();
		if (head) head.classList.toggle('is-scrolled', panel.scrollTop > 0);
	};

	const close = (wrap, { restoreFocus = true } = {}) => {
		wrap.classList.remove('is-in');
		setScrollLock(wrap, false);
		delete document.body.dataset.dialogOpen;
		wrap._exitTimer = window.setTimeout(() => { wrap.hidden = true; }, EXIT_MS);
		// When a drawer row opened the dialog the drawer is now hidden, so fall
		// back to the menu toggle rather than focusing an element that cannot take it.
		const back = wrap._returnTo;
		if (restoreFocus && back && document.contains(back)) {
			(back.offsetParent !== null ? back : document.querySelector('[data-awdd-menu-toggle]') || back).focus();
		}
		wrap._returnTo = null;
		if (current === wrap) current = null;
		wrap.dispatchEvent(new CustomEvent('awdd-dialog-close'));
	};

	document.querySelectorAll('.dlg-wrap').forEach((wrap) => {
		const panel = wrap.querySelector('.dlg');
		const head = wrap.querySelector('.dlg-head');
		if (!panel) return;

		// A press that starts on the backdrop closes; one that starts inside the
		// panel and is dragged out does not.
		wrap.addEventListener('mousedown', (e) => {
			if (e.target === wrap) close(wrap);
		});
		wrap.querySelectorAll('[data-awdd-dialog-close]').forEach((btn) => btn.addEventListener('click', () => close(wrap)));
		panel.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				e.stopPropagation();
				close(wrap);
				return;
			}
			trapFocus(panel, e);
		});
		if (head) {
			panel.addEventListener('scroll', () => head.classList.toggle('is-scrolled', panel.scrollTop > 0), { passive: true });
		}
	});

	// Fallback for when focus has left the panel while it is open: the callback
	// form re-renders over AJAX, which drops the focused element to the body, and
	// the panel's own keydown handler never hears from the body. Escape inside
	// the panel is already handled (and stopped) there, so it is skipped here.
	const panelOf = (wrap) => wrap.querySelector('.dlg');
	const focusPanel = () => {
		if (!current) return;
		const panel = panelOf(current);
		if (!panel || panel.contains(document.activeElement)) return;
		(focusables(panel)[0] || panel).focus({ preventScroll: true });
	};

	document.addEventListener('keydown', (e) => {
		if (!current) return;
		const panel = panelOf(current);
		if (!panel) return;
		const inside = panel.contains(document.activeElement);
		if (e.key === 'Escape') {
			if (!inside) close(current);
			return;
		}
		if (e.key === 'Tab' && !inside) {
			e.preventDefault();
			focusPanel();
		}
	});

	// Gravity Forms swaps the form markup after an AJAX submit, so focus is
	// pulled back to the panel once it has rendered. These are jQuery events, so
	// there is nothing to listen for without jQuery.
	if (window.jQuery) {
		window.jQuery(document).on('gform_confirmation_loaded gform_post_render', focusPanel);
	}

	openByName = (name) => {
		const wrap = document.getElementById(`awdd-dialog-${name}`);
		if (!wrap) return false;
		open(wrap, null);
		return true;
	};

	document.addEventListener('click', (e) => {
		const opener = e.target.closest('[data-awdd-dialog-open]');
		if (!opener) return;
		const wrap = document.getElementById(`awdd-dialog-${opener.dataset.awddDialogOpen}`);
		if (!wrap) return;
		// Let the link navigate on ctrl/meta/shift/alt or a non-primary button.
		if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
		e.preventDefault();
		open(wrap, opener);
	});
}
